<template>
  <v-container
    v-if="loading"
    class="my-3"
  >   
    <v-row>
      <v-col
        v-for="n in noOfSkeletonLoaders"
        :key="n"
        cols="12"
        sm="6"
        lg="4"
        xl="3"
      >
        <v-skeleton-loader
          color="grey lighten-2"
          class="mx-auto"
          max-width="360"
          type="card"
        />
      </v-col>
    </v-row>
  </v-container>
  <carousel-3d
    v-else
    :disable3d="false"
    :space="370"
    :clickable="true"
    :controls-visible="true"
    height="295px"
  > 
    <div
      v-if="leaveEntitlements.length <= 0"
      :index="0"
      class="no-data-div"
    >
      No entitlements available
    </div>
    <slide
      v-for="(item, i) in leaveEntitlements"
      :key="i"
      :index="i"
      :count="leaveEntitlements.length"
    >
      <v-card
        class="carousel-card"
      >
        <v-card-title
          primary-title
          class="justify-center"
        >
          <span class="px-1">
            {{ item.leave_type.name }}
          </span>
        </v-card-title>

        <v-card-title class="blue--text display-2 justify-center py-2">
          {{ getNameInitials(item.leave_type.name) }}
        </v-card-title>

        <v-container class="py-0 text-center">
          <v-row no-gutters> 
            <v-col cols="12">
              <span class="font-weight-bold">
                {{ item.year }} 
              </span>
            </v-col>
          </v-row>
        </v-container>
        
        <v-container class="text-center">
          <v-row no-gutters>
            <v-col
              cols="5"
              class="text-left pl-5"
            >
              <span>Entitlement</span>
            </v-col>
            <v-col
              cols="2"
              class="text-center"
            >
              :
            </v-col>
            <v-col
              cols="5"
              class="text-right pr-7"
            >
              <span class="font-weight-black">
                {{ (parseFloat(item.entitled_days_taken || 0) + parseFloat(item.carried_forward_days_taken || 0)+parseFloat(item.available_days || 0)).toFixed(2) }}
              </span>
            </v-col>
            
            <v-col
              cols="5"
              class="text-left pl-5"
            >
              <span>Leave Utilized</span>
            </v-col>
            <v-col
              cols="2"
              class="text-center"
            >
              :
            </v-col>
            <v-col
              cols="5"
              class="text-right pr-7"
            >
              <span class="font-weight-black">
                {{ (parseFloat(item.entitled_days_taken || 0) + parseFloat(item.carried_forward_days_taken || 0)).toFixed(2) }}
              </span>
            </v-col>

            <v-col
              cols="5"
              class="text-left pl-5"
            >
              <span>Leave Balance</span>
            </v-col>
            <v-col
              cols="2"
              class="text-center"
            >
              :
            </v-col>
            <v-col
              cols="5"
              class="text-right pr-7"
            >
              <span class="font-weight-black">
                {{ item.available_days ? parseFloat(item.available_days).toFixed(2) : '0.00' }}
              </span>
            </v-col>
          </v-row>
        </v-container>

        <v-container
          v-if="item.leave_type.is_carry_forwardable"
        >
          <v-row no-gutters>
            <v-col
              cols="5"
              class="text-left font-weight-bold"
            >
              <i style="font-size:12px"> Brought Forward: {{ item.carried_forward_days ? parseFloat(item.carried_forward_days).toFixed(2) : '0.00' }}
              </i>
            </v-col>
            <v-col
              cols="7"
              class="text-right font-weight-bold"
            >
              <i style="font-size:12px"> Max Carry Forward Days: {{ item.maximum_carry_forward_amount ? parseFloat(item.maximum_carry_forward_amount).toFixed(2) : '0.00' }}
              </i>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </slide>    
  </carousel-3d>
</template>

<script>
  import { Carousel3d, Slide } from 'vue-carousel-3d'
  export default {
    components: {
      Carousel3d,
      Slide,
    },
    props: {
      employeeUid: {
        type: String,
        default: '',
      },
    },
    data () {
      return {
        menu: [],
        leaveEntitlements: [],
        loading: false,
      }
    },
    computed: {
      noOfSkeletonLoaders () {
        switch (this.$vuetify.breakpoint.name) {
          case 'xs': return 1
          case 'sm': return 2
          case 'md': return 2
          case 'lg': return 3
        }

        return 4 // xl
      },
    },
    watch: {
      employeeUid: {
        immediate: true,
        handler (value) {
          if (value) {
            this.loadData()
          }
        },
      },
    },
    methods: {
      roundLeave (days) {
        return (days - Math.floor(days)) >= 0.5 ? Math.floor(days) + 0.5 : Math.floor(days)
      },
      loadData () {
        this.loading = true
        Promise.all([
          this.$api.myLeaveEntitlements().list(),
          this.$api.leaveApplications().list(this.employeeUid),
        ]).then(([leaveEntitlement, leaveApplication]) => {
          let annualLeaveApplications = null
          let sickLeaveApplications = null
          let unpaidLeaveApplications = null
          let maternityLeaveApplications = null
          let paternityLeaveApplications = null
          let compassionateLeaveApplications = null
          let nextYearAnnualLeaveApplications = null
          let nextYearSickLeaveApplications = null
          let nextYearMaternityLeaveApplications = null
          let nextYearUnpaidLeaveApplications = null
          let nextYearPaternityLeaveApplications = null
          let nextYearCompassionateLeaveApplications = null

          let annualLeaveCounter = 0
          let sickLeaveCounter = 0
          let maternityLeaveCounter = 0
          let paternityLeaveCounter = 0
          let compassionateLeaveCounter = 0
          let unpaidLeaveCounter = 0
          let nextYearAnnualLeaveCounter = 0
          let nextYearSickLeaveCounter = 0
          let nextYearMaternityLeaveCounter = 0
          let nextYearPaternityLeaveCounter = 0
          let nextYearCompassionateLeaveCounter = 0
          let nextYearUnpaidLeaveCounter = 0

          leaveApplication.data.map(item => {
            if (item.leave_type.name.toLowerCase().includes('annual') && item.year === (new Date()).getFullYear()) {
              annualLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('annual') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearAnnualLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === (new Date()).getFullYear()) {
              sickLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearSickLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === (new Date()).getFullYear()) {
              unpaidLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearUnpaidLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === (new Date()).getFullYear()) {
              maternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearMaternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === (new Date()).getFullYear()) {
              paternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearPaternityLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === (new Date()).getFullYear()) {
              compassionateLeaveApplications = item.leave_applications
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === ((new Date()).getFullYear() + 1)) {
              nextYearCompassionateLeaveApplications = item.leave_applications
            }
          })

          if (annualLeaveApplications) { annualLeaveCounter = this.getPendingApplications(annualLeaveApplications) }
          if (nextYearAnnualLeaveApplications) { nextYearAnnualLeaveCounter = this.getPendingApplications(nextYearAnnualLeaveApplications) }

          if (sickLeaveApplications) { sickLeaveCounter = this.getPendingApplications(sickLeaveApplications) }
          if (nextYearSickLeaveApplications) { nextYearSickLeaveCounter = this.getPendingApplications(nextYearSickLeaveApplications) }

          if (unpaidLeaveApplications) { unpaidLeaveCounter = this.getPendingApplications(unpaidLeaveApplications) }
          if (nextYearUnpaidLeaveApplications) { nextYearUnpaidLeaveCounter = this.getPendingApplications(nextYearUnpaidLeaveApplications) }

          if (maternityLeaveApplications) { maternityLeaveCounter = this.getPendingApplications(maternityLeaveApplications) }
          if (nextYearMaternityLeaveApplications) { nextYearMaternityLeaveCounter = this.getPendingApplications(nextYearMaternityLeaveApplications) }

          if (paternityLeaveApplications) { paternityLeaveCounter = this.getPendingApplications(paternityLeaveApplications) }
          if (nextYearPaternityLeaveApplications) { nextYearPaternityLeaveCounter = this.getPendingApplications(nextYearPaternityLeaveApplications) }

          if (compassionateLeaveApplications) { compassionateLeaveCounter = this.getPendingApplications(compassionateLeaveApplications) }
          if (nextYearCompassionateLeaveApplications) { nextYearCompassionateLeaveCounter = this.getPendingApplications(nextYearCompassionateLeaveApplications) }

          leaveEntitlement.data.map(item => {
            if (item.leave_type.name.toLowerCase().includes('annual') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'annual', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - annualLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('annual') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearAnnualLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'sick', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - sickLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('sick') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearSickLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'unpaid', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - unpaidLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('unpaid') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearUnpaidLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'maternity', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - maternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('maternity') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearMaternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'paternity', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - paternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('paternity') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearPaternityLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === (new Date()).getFullYear()) {
              const nextYearEntitlement = this.getNextYearEntitlement(leaveEntitlement, 'compassionate', item)
              const carriedOverDays = nextYearEntitlement ? nextYearEntitlement.carried_forward_days : 0
              item.available_days = item.available_days - carriedOverDays - compassionateLeaveCounter
            } else if (item.leave_type.name.toLowerCase().includes('compassionate') && item.year === ((new Date()).getFullYear() + 1)) {
              item.available_days = item.available_days - nextYearCompassionateLeaveCounter
            }
          })

          let annualLeave = leaveEntitlement.data.find(entitlement => entitlement.leave_type_id === 1) // annual leave
          let unpaidLeave = leaveEntitlement.data.find(entitlement => entitlement.leave_type_id === 3) // unpaid leave

          if (annualLeave && unpaidLeave) {
            if (annualLeave.available_days < 0.5) {
              this.leaveEntitlements = leaveEntitlement.data.filter(entitlement => {
                return entitlement.leave_type_id !== 1
              })
            } else {
              this.leaveEntitlements = leaveEntitlement.data.filter(entitlement => {
                return entitlement.leave_type_id !== 3
              })
            }
          } else {
            this.leaveEntitlements = leaveEntitlement.data
          }

          this.loading = false
        })
      },

      getNameInitials (string) {
        return _.words(string).reduce((accumulator, currentValue) => {
          accumulator += currentValue.charAt(0).toUpperCase()
          
          return accumulator
        }, '')
      },
      getNextYearEntitlement (leaveEntitlement, leaveType, item) {
        return leaveEntitlement.data.find(entitlement => entitlement.leave_type.name.toLowerCase().includes(leaveType) && entitlement.year === item.year + 1)
      },

      getPendingApplications (leaveApplications) {
        let sum = 0
        leaveApplications.map(application => {
          if (application.approval_request.approvals[application.approval_request.approvals.length - 1].status.id === 100) {
            sum = sum + application.requested_days
          }
        })

        return sum
      },
    },
  }
</script>

<style scoped>
  .no-data-div {
    height: 295px;
    text-align:center; 
    vertical-align: center; 
    line-height: 295px;
  }
  
  .carousel-card {  
    height:295px; 
    width: 360px;
  }
</style>
